import { useState, useContext, useReducer, useEffect, useRef } from 'react';
import { cleanAdminUserFormFields } from '../domain/adminUser/cleanAdminUserFormFields';
import { useStudentRoleSelection } from '../hooks/useStudentRoleSelection';
import { AuthContext } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useNavigate } from 'react-router-dom';
import LocationDropdowns from '../components/LocationDropdowns';
import SearchableSelect from '../components/Form/SearchableSelect';
import { AdminUserFormShell, AdminUserBasicInfoSection, AdminUserAdditionalContactFields, AdminUserFormFlowNav } from '../components/AdminUserForm';
import { batchesAPI, enrollmentsAPI, authAPI, mastersAPI } from '../services/api';
import AddEnrollmentModal from '../components/AddEnrollmentModal';
import StudentIdIntakeCheckpoint from '../components/StudentIdIntakeCheckpoint';
import { useUnixIdentityEnabled } from '../hooks/useUnixIdentityEnabled';
import { useStudentIdPreview } from '../hooks/useStudentIdPreview';
import {
  requiresTypedStudentId,
  showsTypedStudentId,
  categoryTypeForStudentId,
  referralLabelFromHeardFrom,
  STUDENT_ID_INTAKE_MODES,
} from '../domain/identity/studentIdIntake';
import { liveGeneratedIdentity, formatRegisteredUserClipboardText } from '../domain/identity/generatedIdOverlayCopy';
import { EMPTY_ENROLLMENT_FIELDS, enrollDisplayFromSelection, enrollmentFieldsFromBatch, identityInputFromFilledEnrollment, programTypeLabel } from '../domain/adminUser/enrollSequence';
import {
  initialFormData,
  formReducer,
  useFormData,
  DateInput,
  EducationFields,
  PlacementFields,
  MarksFields
} from '../components/AdminAddUserForm';
import EditableEnrollFields from '../components/AdminAddUserForm/EditableEnrollFields';
import SuggestedStudentIdHint from '../components/AdminAddUserForm/SuggestedStudentIdHint';
import ReadOnlyEnrollField from '../components/AdminAddUserForm/ReadOnlyEnrollField';
import { ensureEnrollmentBatchFromForm } from '../hooks/useEnsureEnrollmentBatch';
import GeneratedIdGuideOverlay from '../components/GeneratedIdGuideOverlay';
import {
  canCreateEnrollmentBatch,
  isLeadIntakeMode,
  missingEnrollmentFieldsForSave,
} from '../domain/adminUser/enrollmentRequirement';
import { isPrivilegedStaffRole } from '../utils/permissions';
import { BATCH_SEARCHABLE_SELECT_PROPS } from '../domain/batch/batchDisplayLabel';

const REGISTER_RESULT_ID = 'auf-register-result';

const AdminAddUserPage = () => {
  const { user } = useContext(AuthContext);
  const { currentTheme } = useTheme();
  const token = user?.token || user?.user?.token;
  const userRole = user?.role || user?.user?.role;
  const navigate = useNavigate();


  const batchSelectionRequest = useRef(0);
  const [formData, dispatch] = useReducer(formReducer, initialFormData);
  const [message, setMessage] = useState('');
  const [showAdditionalFields, setShowAdditionalFields] = useState(false);
  const [postRegisterFlow, setPostRegisterFlow] = useState(null);
  const [showPostAddEnrollmentModal, setShowPostAddEnrollmentModal] = useState(false);
  const [copyFeedback, setCopyFeedback] = useState('');

  // Use custom hook for data fetching
  const {
    courses,
    coursesLoading,
    roles,
    educationOptions,
    universityOptions,
    companyOptions,
    heardFromOptions,
    courseCategories,
    domains,
    batches,
    counselors,
    refreshCompanyOptions,
    refreshBatches,
  } = useFormData(token);

  // Ensure all data is arrays (defensive programming)
  const safeCourseCategories = Array.isArray(courseCategories) ? courseCategories : [];
  const safeDomains = Array.isArray(domains) ? domains : [];
  const safeBatches = Array.isArray(batches) ? batches : [];
  const safeCounselors = Array.isArray(counselors) ? counselors : [];
  const safeCourses = Array.isArray(courses) ? courses : [];


  const { studentRoleId, isStudentRole } = useStudentRoleSelection(roles, formData.role);
  const unixIdentity = useUnixIdentityEnabled();
  const isLeadStudent =
    isStudentRole && unixIdentity && isLeadIntakeMode(formData.studentIdIntakeMode);
  const [creatingBatch, setCreatingBatch] = useState(false);
  const [createBatchMessage, setCreateBatchMessage] = useState('');

  useEffect(() => {
    if (!studentRoleId) return;
    if (formData.role && formData.role !== 'student') return;
    if (String(formData.role) === String(studentRoleId)) return;
    dispatch({ type: 'UPDATE_FIELD', field: 'role', value: studentRoleId });
  }, [studentRoleId, formData.role]);
  const selectedCourse = safeCourses.find(
    (course) => String(course?.id) === String(formData.courseId)
  );
  const selectedCategory =
    safeCourseCategories.find(
      (category) => String(category?.id) === String(formData.courseCategoryId)
    ) || selectedCourse?.category;
  const selectedBatch = safeBatches.find(
    (batch) => String(batch?.id) === String(formData.batchId)
  );
  const fromFilled = identityInputFromFilledEnrollment({
    formData,
    batch: selectedBatch,
    selectedCategory,
  });
  const generateCategoryType =
    fromFilled.categoryType || categoryTypeForStudentId(selectedCategory);
  const derivedProgramType = programTypeLabel(generateCategoryType);
  const derivedCourseName =
    (typeof selectedCourse === 'object' && selectedCourse
      ? selectedCourse.courseName || selectedCourse.name
      : '') || '';
  const derivedCategoryName = (() => {
    if (!selectedCategory || typeof selectedCategory !== 'object') return '';
    const name = selectedCategory.categoryName || selectedCategory.name || '';
    const code = selectedCategory.categoryCode || selectedCategory.code || '';
    return code ? `${name} - ${code}` : name;
  })();
  const canSuggestStudentId =
    isStudentRole &&
    Boolean(fromFilled.modeOfCourse) &&
    Boolean(fromFilled.categoryType) &&
    (String(fromFilled.modeOfCourse).trim().toLowerCase() !== 'offline' ||
      Boolean(fromFilled.locationOfCenter));
  const { previewId, previewUnixUsername, previewBatchId } = useStudentIdPreview({
    enabled:
      canSuggestStudentId &&
      (!unixIdentity ||
        formData.studentIdIntakeMode === STUDENT_ID_INTAKE_MODES.GENERATE ||
        (formData.studentIdIntakeMode === STUDENT_ID_INTAKE_MODES.EXISTING &&
          Boolean(String(formData.veStudentId || '').trim()))),
    courseId: formData.courseId,
    courseCategoryId: formData.courseCategoryId,
    programType: generateCategoryType,
    modeOfCourse: fromFilled.modeOfCourse,
    locationOfCenter: fromFilled.locationOfCenter,
    heardFromId: formData.heardFrom,
    firstName: formData.firstName,
    lastName: formData.lastName,
    batchId: formData.batchId,
    batchStartDate: formData.batchStartDate,
    veStudentId:
      formData.studentIdIntakeMode === STUDENT_ID_INTAKE_MODES.EXISTING
        ? formData.veStudentId
        : '',
  });
  const referralLabel = referralLabelFromHeardFrom(heardFromOptions, formData.heardFrom);
  const liveIdentity = liveGeneratedIdentity({
    categoryType: fromFilled.categoryType,
    categoryCode: fromFilled.categoryCode,
    modeOfCourse: fromFilled.modeOfCourse,
    locationOfCenter: fromFilled.locationOfCenter,
    referralLabel,
    firstName: formData.firstName,
    lastName: formData.lastName,
    batchNumber: fromFilled.batchNumber,
    batchName: fromFilled.batchName,
    batchId: formData.batchId,
    previewBatchId,
    previewId,
    previewUnixUsername,
  });
  const enrollDisplay = enrollDisplayFromSelection({
    batch: selectedBatch,
    formData,
    fallbackProgramType: derivedProgramType,
    fallbackCourseName: derivedCourseName,
    fallbackCategoryName: derivedCategoryName,
  });
  const overlaySelection = {
    programType: enrollDisplay.programType,
    courseName: enrollDisplay.courseName,
    mode: enrollDisplay.mode,
    center: enrollDisplay.center,
    referralLabel,
    batchName: fromFilled.batchName,
    personName: [formData.firstName, formData.lastName].filter(Boolean).join(' '),
  };

  const handleChange = (e) => {
    const fieldName = e.target.name;
    const fieldValue = e.target.value;
    
    dispatch({ type: 'UPDATE_FIELD', field: fieldName, value: fieldValue });
    
    // Diploma No only clears the diploma semester. B.Tech specialization stays.
    if (fieldName === 'diplomaDone' && fieldValue === 'No') {
      dispatch({ type: 'UPDATE_FIELD', field: 'semester', value: '' });
    }
    
    if (fieldName === 'batchId' && fieldValue) {
      const listed = safeBatches.find((batch) => String(batch?.id) === String(fieldValue));
      dispatch({
        type: 'UPDATE_FIELDS',
        fields: {
          ...EMPTY_ENROLLMENT_FIELDS,
          ...enrollmentFieldsFromBatch(listed),
        },
      });
      handleBatchSelection(fieldValue);
    }
    if (fieldName === 'batchId' && !fieldValue) {
      dispatch({
        type: 'UPDATE_FIELDS',
        fields: { ...EMPTY_ENROLLMENT_FIELDS },
      });
    }
  };

  const handleBatchSelection = async (batchId) => {
    const requestId = ++batchSelectionRequest.current;
    try {
      const response = await batchesAPI.getById(batchId);
      if (requestId !== batchSelectionRequest.current) return;
      const batch = response.data?.data || response.data;
      const fields = enrollmentFieldsFromBatch(batch);
      dispatch({
        type: 'UPDATE_FIELDS',
        fields: { ...EMPTY_ENROLLMENT_FIELDS, ...fields },
      });
    } catch (error) {
      console.error('Error fetching batch details:', error);
    }
  };

  const handleCreateBatchFromForm = async () => {
    if (!canCreateEnrollmentBatch(formData)) {
      setCreateBatchMessage('Fill course, mode, and batch start date first.');
      return;
    }
    setCreatingBatch(true);
    setCreateBatchMessage('');
    try {
      const resolved = await ensureEnrollmentBatchFromForm(formData);
      const batchId = resolved?.id != null ? String(resolved.id) : '';
      if (batchId) {
        dispatch({ type: 'UPDATE_FIELD', field: 'batchId', value: batchId });
        await handleBatchSelection(batchId);
      }
      if (typeof refreshBatches === 'function') await refreshBatches();
      const name = resolved?.batchName || resolved?.name || batchId;
      setCreateBatchMessage(
        resolved?.created
          ? `Created batch “${name}” and selected it.`
          : `Using existing batch “${name}”.`
      );
    } catch (error) {
      setCreateBatchMessage(
        error.response?.data?.message || error.message || 'Could not create batch'
      );
    } finally {
      setCreatingBatch(false);
    }
  };

  // Registration logic - backend handles User + Student + AcademicQualifications + Enrollment + CounsellingSessions
  const registerUser = async (userData) => {
    const res = await authAPI.register(userData);
    return res.data;
  };

  // Helper function to create or get company ID
  const createOrGetCompanyId = async (companyNameOrId, companyOptions) => {
    if (!companyNameOrId || companyNameOrId.trim() === '') {
      return null;
    }

    // If it's already a numeric ID, return it
    if (/^\d+$/.test(String(companyNameOrId))) {
      return String(companyNameOrId);
    }

    // Check if it matches an existing company
    const matchedCompany = companyOptions.find(opt => {
      const optionValue = typeof opt === 'object' ? opt.value : opt;
      return optionValue.toLowerCase() === companyNameOrId.trim().toLowerCase();
    });

    if (matchedCompany) {
      const optionId = typeof matchedCompany === 'object' ? matchedCompany.id : matchedCompany;
      return String(optionId);
    }

    // Create new company
    try {
      const response = await mastersAPI.createCompany({ companyName: companyNameOrId.trim() });

      if (response.data && response.data.id) {
        return String(response.data.id);
      }
    } catch (error) {
      console.error('Error creating company:', error);
      throw new Error(error.response?.data?.message || 'Failed to create company');
    }

    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Frontend validation
    if (!formData.firstName || !formData.lastName || !formData.email || !formData.password || !formData.role) {
      setMessage('❌ Please fill in all required fields (First Name, Last Name, Email, Password, Role)');
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setMessage('❌ Please enter a valid email address');
      return;
    }

    // Password validation
    if (formData.password.length < 6) {
      setMessage('❌ Password must be at least 6 characters long');
      return;
    }

    if (isStudentRole) {
      const missingEnroll = missingEnrollmentFieldsForSave(formData, {
        intakeMode: unixIdentity ? formData.studentIdIntakeMode : undefined,
      });
      if (missingEnroll.length) {
        setMessage(`❌ Please complete enrollment fields: ${missingEnroll.join(', ')}`);
        return;
      }
    }

    if (
      isStudentRole &&
      requiresTypedStudentId(unixIdentity, formData.studentIdIntakeMode) &&
      !String(formData.veStudentId || '').trim()
    ) {
      setMessage('❌ Please enter a Student ID');
      return;
    }

    try {
      if (
        isStudentRole &&
        !String(formData.batchId || '').trim() &&
        canCreateEnrollmentBatch(formData)
      ) {
        const resolved = await ensureEnrollmentBatchFromForm(formData);
        if (resolved?.id) {
          dispatch({ type: 'UPDATE_FIELD', field: 'batchId', value: String(resolved.id) });
          formData.batchId = String(resolved.id);
        }
      }
      // Prepare data for backend - backend's registerUser handles everything in one transaction
      const userData = { ...formData };

      if (userData.studentStatus === 'On Hold' && userData.holdTillDate) {
        const holdRemark = `Hold till: ${userData.holdTillDate}`;
        userData.remarks = userData.remarks ? `${userData.remarks}\n${holdRemark}` : holdRemark;
      }
      
      // Map role to roleId for backend - handle both string and object formats
      let roleId = userData.role;
      if (typeof roleId === 'object' && roleId?.id) {
        roleId = roleId.id;
      }
      userData.roleId = roleId;
      delete userData.role;
      
      // Map other fields to match backend expectations
      if (userData.degree) {
        userData.degreeId = typeof userData.degree === 'object' ? userData.degree.id : userData.degree;
        delete userData.degree;
      }
      if (userData.university) {
        userData.universityId = typeof userData.university === 'object' ? userData.university.id : userData.university;
        delete userData.university;
      }
      if (userData.country) {
        userData.countryId = userData.country;
        delete userData.country;
      }
      if (userData.state) {
        userData.stateId = userData.state;
        delete userData.state;
      }
      if (userData.heardFrom) {
        userData.heardFromId = typeof userData.heardFrom === 'object' ? userData.heardFrom.id : userData.heardFrom;
        delete userData.heardFrom;
      }
      
      // Map phone fields - backend accepts both 'mobile' and 'phoneNumber'
      if (userData.mobile) {
        userData.phoneNumber = userData.mobile; // Backend prefers phoneNumber
      }
      
      // Map address fields - backend accepts both 'address' and 'currentAddress'
      if (userData.address) {
        userData.currentAddress = userData.address; // Backend prefers currentAddress
      } else if (userData.currentCity) {
        userData.currentAddress = userData.currentCity;
      }
      
      // Ensure middleName is included (backend needs it for fullName construction)
      // middleName is already in formData, so it will be included
      
      // Clean up undefined/null/empty string values
      // But preserve marks type fields even if they're empty (they have defaults)
      // Also preserve guardianEmail even if empty (it's optional but should be sent if provided)
      cleanAdminUserFormFields(userData, 'delete');
      if (!unixIdentity) {
        delete userData.studentIdIntakeMode;
      }
      
      // Handle placement - create company if needed before submission
      // Backend expects placement fields with prefix "placement_"
      if (formData.placement && formData.placement.companyName) {
        try {
          // Check if it's a company ID (numeric) or company name (string)
          const isCompanyId = /^\d+$/.test(String(formData.placement.companyName));
          let finalCompanyValue = formData.placement.companyName;
          
          if (!isCompanyId) {
            // It's a company name, create or get the company ID
            const companyId = await createOrGetCompanyId(formData.placement.companyName, companyOptions);
            if (companyId) {
              finalCompanyValue = companyId;
            } else {
              // If company creation failed, use the name (backend will try to create it)
              console.warn('Company creation failed, sending name to backend');
            }
          }
          
          // Map placement data to backend format (with placement_ prefix)
          userData.placement_companyName = finalCompanyValue; // Backend will look up by ID or name
          userData.placement_position = formData.placement.position || null;
          userData.placement_package = formData.placement.package || null;
          userData.placement_location = formData.placement.location || null;
          userData.placement_interviewDate = formData.placement.interviewDate || null;
          userData.placement_result = formData.placement.result || null;
          userData.placement_offerDetails = formData.placement.offerDetails || null;
          userData.placement_placementDate = formData.placement.placementDate || null;
          userData.placement_joiningDate = formData.placement.joiningDate || null;
          userData.placement_shouldMoveToAlumni = formData.placement.shouldMoveToAlumni || 'Yes';
          userData.placement_placementGroupRemovalReason = formData.placement.placementGroupRemovalReason || null;
        } catch (error) {
          setMessage(`❌ ${error.message}`);
          return;
        }
      }
      // Remove nested placement object (we've flattened it above)
      delete userData.placement;

      if (formData.appliedForTruechip != null && String(formData.appliedForTruechip).trim() !== '') {
        userData.appliedForTruechip = formData.appliedForTruechip;
      }
      if (formData.truechipResult != null && String(formData.truechipResult).trim() !== '') {
        userData.truechipResult = formData.truechipResult;
      }
      const ir = formData.interviewReadiness || {};
      if (ir.inMockGroup != null && String(ir.inMockGroup).trim() !== '') {
        userData.inMockGroup = ir.inMockGroup;
      }
      if (ir.inUnderpreparedGroup != null && String(ir.inUnderpreparedGroup).trim() !== '') {
        userData.inUnderpreparedGroup = ir.inUnderpreparedGroup;
      }
      if (ir.inPlacementGroup != null && String(ir.inPlacementGroup).trim() !== '') {
        userData.inPlacementGroup = ir.inPlacementGroup;
      }
      
      // Remove other nested objects that backend doesn't expect
      delete userData.training;
      delete userData.performance;
      delete userData.projects;
      delete userData.interviewReadiness;
      delete userData.testimony;
      
      // Call registerUser - backend will create User + Student + AcademicQualifications + Enrollment + CounsellingSessions
      const response = await registerUser(userData);
      
      if (!response) {
        throw new Error('Registration failed - no response received');
      }

      setMessage('✅ User and student record created successfully');

      const newStudentId = response.user?.studentId;
      const newUnixUsername = response.user?.unixUsername;
      const savedDetails = {
        studentId: newStudentId || '',
        unixUsername: newUnixUsername || response.user?.unix_username || '',
        email: formData.email || response.user?.email || '',
        name:
          [formData.firstName, formData.middleName, formData.lastName].filter(Boolean).join(' ') ||
          response.user?.name ||
          '',
        role: formData.role || response.user?.role || 'student',
        password: formData.password || '',
      };
      if (isStudentRole && newStudentId) {
        try {
          const er = await enrollmentsAPI.getByStudent(newStudentId);
          setPostRegisterFlow({
            ...savedDetails,
            enrollments: er.data || [],
          });
        } catch {
          setPostRegisterFlow({
            ...savedDetails,
            enrollments: [],
          });
        }
      } else {
        setPostRegisterFlow({
          ...savedDetails,
          enrollments: [],
        });
      }

      dispatch({ type: 'RESET' });
      setCopyFeedback('');
      window.requestAnimationFrame(() => {
        document.getElementById(REGISTER_RESULT_ID)?.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      });
    } catch (err) {
      console.error('Registration error:', err);
      
      // Handle validation errors with detailed messages
      if (err.response?.data?.errors && Array.isArray(err.response.data.errors)) {
        const errorMessages = err.response.data.errors.map(error => 
          `• ${error.field}: ${error.message}`
        ).join('\n');
        setMessage(`❌ Validation failed:\n${errorMessages}`);
      } else if (err.response?.data?.message) {
        setMessage(`❌ ${err.response.data.message}`);
      } else {
        setMessage('❌ Failed to add user');
      }
    }
  };

  if (!isPrivilegedStaffRole(userRole)) {
    navigate('/dashboard');
    return null;
  }

  return (
    <AdminUserFormShell
      pageTitle="Add New User"
      pageSubtitle="Create a new user account in the system"
      formTitle="Add New User"
      message={message}
      messageId={REGISTER_RESULT_ID}
      onSubmit={handleSubmit}
      afterForm={
        <>
          {isStudentRole && liveIdentity.studentId ? (
            <GeneratedIdGuideOverlay
              studentId={liveIdentity.studentId}
              unixUsername={unixIdentity ? liveIdentity.unixUsername : ''}
              selection={overlaySelection}
            />
          ) : null}
          <AddEnrollmentModal
            isOpen={showPostAddEnrollmentModal}
            onClose={() => setShowPostAddEnrollmentModal(false)}
            studentId={postRegisterFlow?.studentId}
            enrollments={postRegisterFlow?.enrollments || []}
            batches={safeBatches}
            onSuccess={async () => {
              if (!postRegisterFlow?.studentId) return;
              try {
                const er = await enrollmentsAPI.getByStudent(postRegisterFlow.studentId);
                setPostRegisterFlow((prev) =>
                  prev
                    ? {
                        ...prev,
                        enrollments: er.data || [],
                      }
                    : prev
                );
              } catch {
                /* keep previous enrollments list */
              }
            }}
          />
        </>
      }
    >
        {postRegisterFlow && (
          <div
            id="auf-register-success-card"
            className="mb-6 p-4 rounded-lg scroll-mt-28"
            style={{
              backgroundColor: 'var(--color-surface)',
              border: '1px solid var(--color-border)'
            }}
          >
            <p className="font-medium mb-2" style={{ color: 'var(--color-text)' }}>
              Account created
            </p>
            <dl className="text-sm mb-3 space-y-1 font-mono" style={{ color: 'var(--color-text)' }}>
              {postRegisterFlow.name ? (
                <div>
                  <dt className="inline font-sans font-medium">Name: </dt>
                  <dd className="inline">{postRegisterFlow.name}</dd>
                </div>
              ) : null}
              {postRegisterFlow.email ? (
                <div>
                  <dt className="inline font-sans font-medium">Email: </dt>
                  <dd className="inline">{postRegisterFlow.email}</dd>
                </div>
              ) : null}
              {postRegisterFlow.studentId ? (
                <div>
                  <dt className="inline font-sans font-medium">Student ID: </dt>
                  <dd className="inline">{postRegisterFlow.studentId}</dd>
                </div>
              ) : null}
              {postRegisterFlow.unixUsername ? (
                <div>
                  <dt className="inline font-sans font-medium">Unix username: </dt>
                  <dd className="inline">{postRegisterFlow.unixUsername}</dd>
                </div>
              ) : null}
              {postRegisterFlow.password ? (
                <div>
                  <dt className="inline font-sans font-medium">Password: </dt>
                  <dd className="inline">{postRegisterFlow.password}</dd>
                </div>
              ) : null}
            </dl>
            <p className="text-sm mb-3" style={{ color: 'var(--color-text-secondary)' }}>
              {postRegisterFlow.studentId
                ? 'Copy these details to share or paste elsewhere. You can also add another batch enrollment without removing the first.'
                : 'Copy these details to share or paste elsewhere.'}
            </p>
            <div className="flex flex-wrap gap-2 items-center">
              <button
                type="button"
                onClick={async () => {
                  const text = formatRegisteredUserClipboardText(postRegisterFlow);
                  try {
                    await navigator.clipboard.writeText(text);
                    setCopyFeedback('Copied');
                  } catch {
                    setCopyFeedback('Copy failed — select the text above manually');
                  }
                }}
                className="px-4 py-2 rounded-md text-sm font-medium text-white"
                style={{ backgroundColor: 'var(--color-primary)' }}
              >
                Copy user details
              </button>
              {postRegisterFlow.studentId ? (
                <button
                  type="button"
                  onClick={() => setShowPostAddEnrollmentModal(true)}
                  className="px-4 py-2 rounded-md text-sm font-medium"
                  style={{
                    backgroundColor: 'var(--color-surface-secondary)',
                    color: 'var(--color-text)',
                    border: '1px solid var(--color-border)',
                  }}
                >
                  Add enrollment
                </button>
              ) : null}
              <button
                type="button"
                onClick={() => {
                  setPostRegisterFlow(null);
                  setShowPostAddEnrollmentModal(false);
                  setCopyFeedback('');
                }}
                className="px-4 py-2 rounded-md text-sm font-medium"
                style={{
                  backgroundColor: 'var(--color-surface-secondary)',
                  color: 'var(--color-text)'
                }}
              >
                Done
              </button>
              {copyFeedback ? (
                <span className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>
                  {copyFeedback}
                </span>
              ) : null}
            </div>
          </div>
        )}

        <AdminUserFormFlowNav
          isStudent={isStudentRole}
          mode="add"
          enabled
          additionalExpanded={showAdditionalFields}
          onExpandAdditional={setShowAdditionalFields}
        />

        <AdminUserBasicInfoSection
          layoutVariant="add"
          formData={formData}
          onChange={handleChange}
          roles={roles}
          isStudentRole={isStudentRole}
          afterRole={
            isStudentRole ? (
              unixIdentity ? (
                <StudentIdIntakeCheckpoint
                  mode={formData.studentIdIntakeMode}
                  typedId={formData.veStudentId}
                  onModeChange={handleChange}
                  onTypedIdChange={handleChange}
                  requireTypedId={requiresTypedStudentId(
                    true,
                    formData.studentIdIntakeMode
                  )}
                  showTypedId={showsTypedStudentId(true, formData.studentIdIntakeMode)}
                  previewUnixUsername={previewUnixUsername}
                />
              ) : (
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-2" style={{ color: 'var(--color-text)' }}>
                    VE Student ID <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="veStudentId"
                    value={formData.veStudentId}
                    onChange={handleChange}
                    placeholder={
                      liveIdentity.studentId
                        ? `Suggested: ${liveIdentity.studentId}`
                        : 'Fill program, mode, and center to see the next Student ID'
                    }
                    required
                    className="w-full px-4 py-3 theme-input rounded-lg theme-focus"
                    style={{
                      backgroundColor: 'var(--color-surface)',
                      border: '2px solid var(--color-border)',
                      color: 'var(--color-text)',
                    }}
                  />
                  <div className="mt-2">
                    <SuggestedStudentIdHint
                      studentId={liveIdentity.studentId}
                      showUse={
                        Boolean(liveIdentity.studentId) &&
                        String(formData.veStudentId || '').trim() !== liveIdentity.studentId
                      }
                      onUse={() =>
                        dispatch({
                          type: 'UPDATE_FIELD',
                          field: 'veStudentId',
                          value: liveIdentity.studentId,
                        })
                      }
                    />
                  </div>
                </div>
              )
            ) : null
          }
        />

        {/* Batch & Course Information - Above Additional Information */}
        {isStudentRole && (
          <div id="auf-batch-course" className="auf-form-section scroll-mt-28 border-t pt-4 mt-4">
            <h3 className="text-lg font-semibold mb-4 theme-text-gradient">Enrollment</h3>
            
            <div className="space-y-4 mb-6">
              <div
                className="rounded-lg p-4 space-y-3"
                style={{
                  backgroundColor: 'var(--color-surface)',
                  border: '1px solid var(--color-border)',
                }}
              >
                <p className="text-sm font-medium" style={{ color: 'var(--color-text)' }}>
                  Batch{isLeadStudent ? ' (optional for Lead)' : ''}
                </p>
                <p className="text-xs mb-2" style={{ color: 'var(--color-text-secondary)' }}>
                  {isLeadStudent
                    ? 'Lead students can be saved without a batch. Fill enrollment fields and use Create batch when ready, or leave blank.'
                    : 'Pick a batch to fill these fields, or edit them. Use Create batch to add a new batch now, or one is created on save if needed.'}
                </p>
                <SearchableSelect
                  name="batchId"
                  value={formData.batchId || ''}
                  onChange={handleChange}
                  className="w-full mb-3"
                  placeholder={isLeadStudent ? 'Select Batch (optional)' : 'Select Batch'}
                  options={safeBatches}
                  {...BATCH_SEARCHABLE_SELECT_PROPS}
                />
                <EditableEnrollFields
                  formData={formData}
                  dispatch={dispatch}
                  courses={safeCourses}
                  courseCategories={safeCourseCategories}
                  batches={safeBatches}
                  batchOptional={isLeadStudent}
                  onCreateBatch={handleCreateBatchFromForm}
                  creatingBatch={creatingBatch}
                  createBatchMessage={createBatchMessage}
                />
                <SuggestedStudentIdHint
                  studentId={liveIdentity.studentId}
                  unixUsername={unixIdentity ? liveIdentity.unixUsername : ''}
                  showUse={
                    !unixIdentity &&
                    Boolean(liveIdentity.studentId) &&
                    String(formData.veStudentId || '').trim() !== liveIdentity.studentId
                  }
                  onUse={() =>
                    dispatch({
                      type: 'UPDATE_FIELD',
                      field: 'veStudentId',
                      value: liveIdentity.studentId,
                    })
                  }
                />
              </div>
            </div>

            {/* Add User Button - Above Additional Fields */}
            <div id="auf-submit-primary" className="auf-form-section scroll-mt-28 mb-6 mt-6">
              <button
                type="submit"
                className="w-full py-4 rounded-lg font-medium transition-all duration-200 theme-hover-lift theme-focus"
                style={{ 
                  backgroundColor: 'var(--color-primary)', 
                  color: 'white'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = 'var(--color-primary-600)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'var(--color-primary)';
                }}
              >
                ➕ Add User
              </button>
            </div>

            {/* Collapsible Additional Fields */}
            <div className="border-t pt-4 mt-4">
              <button
                type="button"
                onClick={() => setShowAdditionalFields(!showAdditionalFields)}
                className="w-full flex items-center justify-between p-3 rounded-lg mb-4 theme-hover-lift"
                style={{ 
                  backgroundColor: 'var(--color-surface)',
                  border: '2px solid var(--color-border)'
                }}
              >
                <span className="font-semibold theme-text">
                  {showAdditionalFields ? '▼' : '▶'} Additional Fields (Fill Later When Editing)
                </span>
                <span className="text-xs text-gray-500">
                  {showAdditionalFields ? 'Click to collapse' : 'Click to expand'}
                </span>
              </button>

              {showAdditionalFields && (
                <div className="space-y-4">
                  <div className="mb-4">
                    <label className="block text-sm font-medium mb-2" style={{ color: 'var(--color-text)' }}>
                      Referred by
                    </label>
                    <SearchableSelect
                      name="heardFrom"
                      value={formData.heardFrom || ''}
                      onChange={handleChange}
                      className="w-full"
                      placeholder="How did you hear about us?"
                      options={heardFromOptions}
                      getOptionValue={(option) => (typeof option === 'object' ? option.id : option)}
                      getOptionLabel={(option) => (typeof option === 'object' ? option.value : option)}
                    />
                  </div>
                  <div className="mb-4">
                    <label className="block text-sm font-medium mb-2" style={{ color: 'var(--color-text)' }}>
                      Comment
                    </label>
                    <textarea
                      name="motivation"
                      placeholder="Comment"
                      value={formData.motivation || ''}
                      onChange={handleChange}
                      className="w-full px-4 py-3 theme-input rounded-lg theme-focus"
                      style={{
                        backgroundColor: 'var(--color-surface)',
                        border: '2px solid var(--color-border)',
                        color: 'var(--color-text)',
                        minHeight: '80px',
                      }}
                    />
                  </div>
                  <AdminUserAdditionalContactFields
                    formData={formData}
                    onChange={handleChange}
                  />
                  <div id="auf-enrollment-status" className="auf-form-section scroll-mt-28 space-y-4">
                  {/* Domain */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium mb-2" style={{ color: 'var(--color-text)' }}>
                      Domain
                    </label>
                    <SearchableSelect
                      name="domainId"
                      value={formData.domainId || ''}
                      onChange={handleChange}
                      className="w-full"
                      placeholder="Select Domain"
                      options={safeDomains}
                      getOptionValue={(domain) => (typeof domain === 'object' ? domain.id : domain)}
                      getOptionLabel={(domain) => (typeof domain === 'object' ? (domain.domainName || domain.name) : domain)}
                    />
                  </div>

                  {/* Attendance Percentage */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium mb-2" style={{ color: 'var(--color-text)' }}>
                      Attendance (%)
                    </label>
                    <input
                      type="number"
                      name="attendancePercentage"
                      placeholder="Enter attendance percentage (0-100)"
                      min="0"
                      max="100"
                      step="0.01"
                      value={formData.attendancePercentage || ''}
                      onChange={handleChange}
                      className="w-full px-4 py-3 theme-input rounded-lg theme-focus"
                      style={{ 
                        backgroundColor: 'var(--color-surface)',
                        border: '2px solid var(--color-border)',
                        color: 'var(--color-text)'
                      }}
                    />
                  </div>

                  {/* Student Status */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium mb-2" style={{ color: 'var(--color-text)' }}>
                      Student Status
                    </label>
                    <SearchableSelect
                      name="studentStatus"
                      value={formData.studentStatus || 'Ongoing'}
                      onChange={handleChange}
                      required
                      className="w-full"
                      options={[
                        { value: 'Ongoing', label: 'Course Ongoing' },
                        { value: 'Completed', label: 'Course Completed' },
                        { value: 'On Hold', label: 'On Hold' },
                        { value: 'Left in between', label: 'Left in between' }
                      ]}
                    />
                  </div>

                  {/* Remarks */}
                  {formData.studentStatus === 'On Hold' && (
                    <div className="mb-4 space-y-3">
                      <label className="block text-sm font-medium mb-2" style={{ color: 'var(--color-text)' }}>
                        Remarks (Reason for hold)
                      </label>
                      <textarea
                        name="remarks"
                        placeholder="Enter reason for hold"
                        value={formData.remarks || ''}
                        onChange={handleChange}
                        className="w-full px-4 py-3 theme-input rounded-lg theme-focus"
                        style={{ 
                          backgroundColor: 'var(--color-surface)',
                          border: '2px solid var(--color-border)',
                          color: 'var(--color-text)'
                        }}
                        rows="3"
                      />
                      <div>
                        <label className="block text-sm font-medium mb-2" style={{ color: 'var(--color-text)' }}>
                          Hold till
                        </label>
                        <input
                          type="date"
                          name="holdTillDate"
                          value={formData.holdTillDate || ''}
                          onChange={handleChange}
                          className="w-full px-4 py-3 theme-input rounded-lg theme-focus"
                          style={{
                            backgroundColor: 'var(--color-surface)',
                            border: '2px solid var(--color-border)',
                            color: 'var(--color-text)'
                          }}
                        />
                      </div>
                    </div>
                  )}

                  {/* Placement Status */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium mb-2" style={{ color: 'var(--color-text)' }}>
                      Placement Status
                    </label>
                    <SearchableSelect
                      name="placementStatus"
                      value={formData.placementStatus || ''}
                      onChange={handleChange}
                      className="w-full"
                      placeholder="Select Placement Status"
                      options={[
                        { value: 'Placed', label: 'Placed' },
                        { value: 'In progress', label: 'In progress' },
                        { value: 'Hold', label: 'Hold' },
                        { value: 'Under Preparation', label: 'Under Preparation' },
                        { value: 'Not interested', label: 'Not interested' }
                      ]}
                    />
                  </div>
                  <div className="mb-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-2" style={{ color: 'var(--color-text)' }}>
                        Applied for Truechip
                      </label>
                      <SearchableSelect
                        name="appliedForTruechip"
                        value={formData.appliedForTruechip || ''}
                        onChange={handleChange}
                        className="w-full"
                        placeholder="—"
                        options={[
                          { value: 'Yes', label: 'Yes' },
                          { value: 'No', label: 'No' },
                        ]}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2" style={{ color: 'var(--color-text)' }}>
                        Truechip result
                      </label>
                      <SearchableSelect
                        name="truechipResult"
                        value={formData.truechipResult || ''}
                        onChange={handleChange}
                        className="w-full"
                        placeholder="—"
                        options={[
                          { value: 'Passed', label: 'Passed' },
                          { value: 'Failed', label: 'Failed' },
                        ]}
                      />
                    </div>
                  </div>
                  <div className="mb-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-2" style={{ color: 'var(--color-text)' }}>
                        In mock group
                      </label>
                      <SearchableSelect
                        name="interviewReadiness.inMockGroup"
                        value={formData.interviewReadiness?.inMockGroup || ''}
                        onChange={(e) =>
                          dispatch({
                            type: 'UPDATE_NESTED_FIELD',
                            section: 'interviewReadiness',
                            field: 'inMockGroup',
                            value: e.target.value,
                          })
                        }
                        className="w-full"
                        placeholder="—"
                        options={[
                          { value: 'Yes', label: 'Yes' },
                          { value: 'No', label: 'No' },
                        ]}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2" style={{ color: 'var(--color-text)' }}>
                        In underprepared group
                      </label>
                      <SearchableSelect
                        name="interviewReadiness.inUnderpreparedGroup"
                        value={formData.interviewReadiness?.inUnderpreparedGroup || ''}
                        onChange={(e) =>
                          dispatch({
                            type: 'UPDATE_NESTED_FIELD',
                            section: 'interviewReadiness',
                            field: 'inUnderpreparedGroup',
                            value: e.target.value,
                          })
                        }
                        className="w-full"
                        placeholder="—"
                        options={[
                          { value: 'Yes', label: 'Yes' },
                          { value: 'No', label: 'No' },
                        ]}
                      />
                    </div>
                  </div>
                  </div>

                  {/* Joining Kit Information */}
                  <div id="auf-joining-kit" className="auf-form-section scroll-mt-28 mb-4 border-t pt-4">
                    <h4 className="font-semibold mb-3 text-sm">📦 Joining Kit</h4>
                    <label className="block mb-2 font-medium text-sm" style={{ color: 'var(--color-text)' }}>
                      Joining Kit to be sent on:
                    </label>
                    <div className="flex gap-4 mb-3">
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name="joiningKitAddress"
                          value="current"
                          checked={formData.joiningKitAddress === 'current'}
                          onChange={handleChange}
                          className="mr-2"
                        />
                        <span style={{ color: 'var(--color-text)' }}>Current Address</span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name="joiningKitAddress"
                          value="permanent"
                          checked={formData.joiningKitAddress === 'permanent'}
                          onChange={handleChange}
                          className="mr-2"
                        />
                        <span style={{ color: 'var(--color-text)' }}>Permanent Address</span>
                      </label>
                    </div>
                    <label className="block mb-2 font-medium text-sm" style={{ color: 'var(--color-text)' }}>
                      Joining Kit Received:
                    </label>
                    <div className="flex gap-4">
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name="joiningKitReceived"
                          value="Yes"
                          checked={formData.joiningKitReceived === 'Yes'}
                          onChange={handleChange}
                          className="mr-2"
                        />
                        <span style={{ color: 'var(--color-text)' }}>Yes</span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name="joiningKitReceived"
                          value="No"
                          checked={formData.joiningKitReceived === 'No'}
                          onChange={handleChange}
                          className="mr-2"
                        />
                        <span style={{ color: 'var(--color-text)' }}>No</span>
                      </label>
                    </div>
                  </div>

                  {/* Parents Information */}
                  <div id="auf-parents" className="auf-form-section scroll-mt-28 mb-4 border-t pt-4">
                    <h4 className="font-semibold mb-3 text-sm">👪 Parents Information</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
                      <input
                        type="text"
                        name="parentsName"
                        placeholder="Parents Name"
                        value={formData.parentsName || ''}
                        onChange={handleChange}
                        className="px-4 py-3 theme-input rounded-lg theme-focus"
                        style={{
                          backgroundColor: 'var(--color-surface)',
                          border: '2px solid var(--color-border)',
                          color: 'var(--color-text)'
                        }}
                      />
                      <input
                        type="text"
                        name="parentsPhone"
                        placeholder="Parents Phone Number"
                        value={formData.parentsPhone || ''}
                        onChange={handleChange}
                        className="px-4 py-3 theme-input rounded-lg theme-focus"
                        style={{
                          backgroundColor: 'var(--color-surface)',
                          border: '2px solid var(--color-border)',
                          color: 'var(--color-text)'
                        }}
                      />
                    </div>
                    <input
                      type="email"
                      name="parentsEmail"
                      placeholder="Parents Email"
                      value={formData.parentsEmail || ''}
                      onChange={handleChange}
                      className="w-full px-4 py-3 theme-input rounded-lg theme-focus"
                      style={{
                        backgroundColor: 'var(--color-surface)',
                        border: '2px solid var(--color-border)',
                        color: 'var(--color-text)'
                      }}
                    />
                  </div>

                  {/* Insurance Information */}
                  <div id="auf-insurance" className="auf-form-section scroll-mt-28 mb-4 border-t pt-4">
                    <h4 className="font-semibold mb-3 text-sm">🛡️ Insurance Information</h4>
                    
                    {/* Insurance Taken */}
                    <div className="mb-4">
                      <label className="block mb-2 font-medium text-sm" style={{ color: 'var(--color-text)' }}>
                        Insurance Taken?
                      </label>
                      <div className="flex gap-4 mb-2">
                        <label className="flex items-center">
                          <input
                            type="radio"
                            name="insuranceTaken"
                            value="Yes"
                            checked={formData.insuranceTaken === 'Yes' || formData.insuranceTaken === true || formData.insuranceTaken === 'true'}
                            onChange={(e) => {
                              const value = e.target.value === 'Yes' ? 'Yes' : 'No';
                              dispatch({ type: 'UPDATE_FIELD', field: 'insuranceTaken', value: value });
                            }}
                            className="mr-2"
                          />
                          <span style={{ color: 'var(--color-text)' }}>Yes</span>
                        </label>
                        <label className="flex items-center">
                          <input
                            type="radio"
                            name="insuranceTaken"
                            value="No"
                            checked={formData.insuranceTaken === 'No' || formData.insuranceTaken === false || formData.insuranceTaken === 'false' || formData.insuranceTaken === '' || formData.insuranceTaken === null || formData.insuranceTaken === undefined}
                            onChange={(e) => {
                              const value = e.target.value === 'No' ? 'No' : 'Yes';
                              dispatch({ type: 'UPDATE_FIELD', field: 'insuranceTaken', value: value });
                            }}
                            className="mr-2"
                          />
                          <span style={{ color: 'var(--color-text)' }}>No</span>
                        </label>
                      </div>
                    </div>
                  </div>

                  {/* Counselling Sessions */}
                  <div id="auf-counselling" className="auf-form-section scroll-mt-28 border-t pt-4 mt-4 mb-4">
                    <h3 className="font-semibold mb-4" style={{ color: 'var(--color-text)' }}>
                      📋 Counselling Sessions
                    </h3>
                    {formData.counsellingSessions.map((session, index) => (
                      <div key={index} className="mb-4 p-4 border rounded-lg" style={{ 
                        backgroundColor: 'var(--color-surface)',
                        borderColor: 'var(--color-border)'
                      }}>
                        <h4 className="font-medium mb-3" style={{ color: 'var(--color-text)' }}>
                          Session {index + 1}
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div>
                            <label className="block text-sm font-medium mb-1" style={{ color: 'var(--color-text)' }}>
                              Date
                            </label>
                            <input
                              type="date"
                              value={session.sessionDate}
                              onChange={(e) => dispatch({
                                type: 'UPDATE_COUNSELLING_SESSION',
                                index,
                                field: 'sessionDate',
                                value: e.target.value
                              })}
                              className="w-full px-3 py-2 theme-input rounded-lg theme-focus"
                              style={{ 
                                backgroundColor: 'var(--color-surface)',
                                border: '2px solid var(--color-border)',
                                color: 'var(--color-text)'
                              }}
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium mb-1" style={{ color: 'var(--color-text)' }}>
                              Counselor Name
                            </label>
                            <SearchableSelect
                              value={session.counselorId}
                              onChange={(e) => dispatch({
                                type: 'UPDATE_COUNSELLING_SESSION',
                                index,
                                field: 'counselorId',
                                value: e.target.value
                              })}
                              className="w-full"
                              placeholder="Select Counselor"
                              options={safeCounselors}
                              getOptionValue={(counselor) => (typeof counselor === 'object' ? counselor.id : counselor)}
                              getOptionLabel={(counselor) =>
                                typeof counselor === 'object'
                                  ? (counselor.counselorName || counselor.name || `${counselor.firstName || ''} ${counselor.lastName || ''}`.trim())
                                  : counselor
                              }
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium mb-1" style={{ color: 'var(--color-text)' }}>
                              Remarks
                            </label>
                            <input
                              type="text"
                              value={session.remarks}
                              onChange={(e) => dispatch({
                                type: 'UPDATE_COUNSELLING_SESSION',
                                index,
                                field: 'remarks',
                                value: e.target.value
                              })}
                              placeholder="Enter remarks"
                              className="w-full px-3 py-2 theme-input rounded-lg theme-focus"
                              style={{ 
                                backgroundColor: 'var(--color-surface)',
                                border: '2px solid var(--color-border)',
                                color: 'var(--color-text)'
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Education Fields */}
                  <div id="auf-education" className="auf-form-section scroll-mt-28 border-t pt-4 mt-4">
                    <EducationFields
                      formData={formData}
                      dispatch={dispatch}
                      educationOptions={educationOptions}
                      universityOptions={universityOptions}
                    />
                    {/* Additional Education Details */}
                    <div className="border-t pt-4 mt-4">
                      <h3 className="font-semibold mb-2">📄 Additional Education Details</h3>

                      <label className="block mb-2 font-medium">Have you done Diploma?</label>
                      <label className="mr-4">
                        <input
                          type="radio"
                          name="diplomaDone"
                          value="Yes"
                          checked={formData.diplomaDone === 'Yes'}
                          onChange={handleChange}
                          className="mr-1"
                        /> Yes
                      </label>
                      <label>
                        <input
                          type="radio"
                          name="diplomaDone"
                          value="No"
                          checked={formData.diplomaDone === 'No'}
                          onChange={handleChange}
                          className="mr-1"
                        /> No
                      </label>

                      {formData.diplomaDone === 'Yes' && (
                        <>
                          <input
                            type="text"
                            name="stream"
                            placeholder="Stream / Specialization"
                            value={formData.stream}
                            onChange={handleChange}
                            className="w-full mb-3 px-4 py-3 theme-input rounded-lg theme-focus"
                            style={{ 
                              backgroundColor: 'var(--color-surface)',
                              border: '2px solid var(--color-border)',
                              color: 'var(--color-text)'
                            }}
                          />

                          <input
                            type="text"
                            name="semester"
                            placeholder="Semester (if not passout)"
                            value={formData.semester}
                            onChange={handleChange}
                            className="w-full mb-3 px-4 py-3 theme-input rounded-lg theme-focus"
                            style={{ 
                              backgroundColor: 'var(--color-surface)',
                              border: '2px solid var(--color-border)',
                              color: 'var(--color-text)'
                            }}
                          />
                        </>
                      )}

                      <MarksFields formData={formData} dispatch={dispatch} />

                      <input
                        type="number"
                        name="tenthYear"
                        placeholder="10th Passing Year"
                        value={formData.tenthYear}
                        onChange={handleChange}
                        className="w-full mb-3 px-4 py-3 theme-input rounded-lg theme-focus"
                        style={{ 
                          backgroundColor: 'var(--color-surface)',
                          border: '2px solid var(--color-border)',
                          color: 'var(--color-text)'
                        }}
                      />

                      <input
                        type="number"
                        name="twelfthYear"
                        placeholder="12th / Diploma Passing Year"
                        value={formData.twelfthYear}
                        onChange={handleChange}
                        className="w-full mb-3 px-4 py-3 theme-input rounded-lg theme-focus"
                        style={{ 
                          backgroundColor: 'var(--color-surface)',
                          border: '2px solid var(--color-border)',
                          color: 'var(--color-text)'
                        }}
                      />

                      <input
                        type="number"
                        name="btechYear"
                        placeholder="B.Tech Passing Year"
                        value={formData.btechYear}
                        onChange={handleChange}
                        className="w-full mb-3 px-4 py-3 theme-input rounded-lg theme-focus"
                        style={{ 
                          backgroundColor: 'var(--color-surface)',
                          border: '2px solid var(--color-border)',
                          color: 'var(--color-text)'
                        }}
                      />
                    </div>
                  </div>

                  {/* Placement Fields */}
                  <div id="auf-placement" className="auf-form-section scroll-mt-28">
                  <PlacementFields
                    formData={formData}
                    dispatch={dispatch}
                    companyOptions={companyOptions}
                    onCompanyOptionsUpdate={refreshCompanyOptions}
                    token={token}
                  />
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
        
        {/* Non-Student Role Collapsible Section */}
        {!isStudentRole && (
          <>
            {/* Add User Button - Above Additional Fields */}
            <div id="auf-submit-primary" className="auf-form-section scroll-mt-28 mb-6 mt-6">
              <button
                type="submit"
                className="w-full py-4 rounded-lg font-medium transition-all duration-200 theme-hover-lift theme-focus"
                style={{ 
                  backgroundColor: 'var(--color-primary)', 
                  color: 'white'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = 'var(--color-primary-600)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'var(--color-primary)';
                }}
              >
                ➕ Add User
              </button>
            </div>

            {/* Collapsible Additional Fields */}
            <div className="border-t pt-4 mt-4">
              <button
                type="button"
                onClick={() => setShowAdditionalFields(!showAdditionalFields)}
                className="w-full flex items-center justify-between p-3 rounded-lg mb-4 theme-hover-lift"
                style={{ 
                  backgroundColor: 'var(--color-surface)',
                  border: '2px solid var(--color-border)'
                }}
              >
                <span className="font-semibold theme-text">
                  {showAdditionalFields ? '▼' : '▶'} Additional Fields (Fill Later When Editing)
                </span>
                <span className="text-xs text-gray-500">
                  {showAdditionalFields ? 'Click to collapse' : 'Click to expand'}
                </span>
              </button>

              {showAdditionalFields && (
                <div className="space-y-4">
                  {/* Additional Personal Information */}
                  <div id="auf-staff-personal" className="auf-form-section scroll-mt-28 border-t pt-4 mt-4">
                    <h3 className="text-lg font-semibold mb-4 theme-text-gradient">Additional Personal Information</h3>
                    <AdminUserAdditionalContactFields
                      formData={formData}
                      onChange={handleChange}
                    />
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      <input
                        type="text"
                        name="currentCity"
                        placeholder="Current City"
                        value={formData.currentCity || ''}
                        onChange={handleChange}
                        className="px-4 py-3 theme-input rounded-lg theme-focus"
                        style={{ 
                          backgroundColor: 'var(--color-surface)',
                          border: '2px solid var(--color-border)',
                          color: 'var(--color-text)'
                        }}
                      />
                      <DateInput
                        name="dob"
                        value={formData.dob}
                        onChange={handleChange}
                        placeholder="Date of Birth"
                      />
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      <input
                        type="text"
                        name="address"
                        placeholder="Current Address"
                        value={formData.address || ''}
                        onChange={handleChange}
                        className="px-4 py-3 theme-input rounded-lg theme-focus"
                        style={{ 
                          backgroundColor: 'var(--color-surface)',
                          border: '2px solid var(--color-border)',
                          color: 'var(--color-text)'
                        }}
                      />
                      <input
                        type="text"
                        name="pincode"
                        placeholder="Pincode"
                        value={formData.pincode || ''}
                        onChange={handleChange}
                        className="px-4 py-3 theme-input rounded-lg theme-focus"
                        style={{ 
                          backgroundColor: 'var(--color-surface)',
                          border: '2px solid var(--color-border)',
                          color: 'var(--color-text)'
                        }}
                      />
                    </div>
                    
                    <div className="mb-4">
                      <input
                        type="text"
                        name="permanentAddress"
                        placeholder="Permanent Address"
                        value={formData.permanentAddress || ''}
                        onChange={handleChange}
                        className="w-full px-4 py-3 theme-input rounded-lg theme-focus"
                        style={{ 
                          backgroundColor: 'var(--color-surface)',
                          border: '2px solid var(--color-border)',
                          color: 'var(--color-text)'
                        }}
                      />
                    </div>
                    
                    <div className="mb-4">
                      <label className="block mb-2 font-medium" style={{ color: 'var(--color-text)' }}>
                        Joining Kit to be sent on:
                      </label>
                      <div className="flex gap-4">
                        <label className="flex items-center">
                          <input
                            type="radio"
                            name="joiningKitAddress"
                            value="current"
                            checked={formData.joiningKitAddress === 'current'}
                            onChange={handleChange}
                            className="mr-2"
                          />
                          <span style={{ color: 'var(--color-text)' }}>Current Address</span>
                        </label>
                        <label className="flex items-center">
                          <input
                            type="radio"
                            name="joiningKitAddress"
                            value="permanent"
                            checked={formData.joiningKitAddress === 'permanent'}
                            onChange={handleChange}
                            className="mr-2"
                          />
                          <span style={{ color: 'var(--color-text)' }}>Permanent Address</span>
                        </label>
                      </div>
                    </div>
                    
                    <div className="mb-4">
                      <label className="block mb-2 font-medium" style={{ color: 'var(--color-text)' }}>
                        Joining Kit Received:
                      </label>
                      <div className="flex gap-4">
                        <label className="flex items-center">
                          <input
                            type="radio"
                            name="joiningKitReceived"
                            value="Yes"
                            checked={formData.joiningKitReceived === 'Yes'}
                            onChange={handleChange}
                            className="mr-2"
                          />
                          <span style={{ color: 'var(--color-text)' }}>Yes</span>
                        </label>
                        <label className="flex items-center">
                          <input
                            type="radio"
                            name="joiningKitReceived"
                            value="No"
                            checked={formData.joiningKitReceived === 'No'}
                            onChange={handleChange}
                            className="mr-2"
                          />
                          <span style={{ color: 'var(--color-text)' }}>No</span>
                        </label>
                      </div>
                    </div>
                  </div>
                  
                  {/* Guardian Information */}
                  <div id="auf-staff-guardian" className="auf-form-section scroll-mt-28 border-t pt-4 mt-4">
                    <h3 className="text-lg font-semibold mb-4 theme-text-gradient">Guardian Information</h3>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      <input
                        type="text"
                        name="guardianName"
                        placeholder="Guardian Name"
                        value={formData.guardianName || ''}
                        onChange={handleChange}
                        className="px-4 py-3 theme-input rounded-lg theme-focus"
                        style={{ 
                          backgroundColor: 'var(--color-surface)',
                          border: '2px solid var(--color-border)',
                          color: 'var(--color-text)'
                        }}
                      />
                      <input
                        type="text"
                        name="guardianContact"
                        placeholder="Guardian Contact Number"
                        value={formData.guardianContact || ''}
                        onChange={handleChange}
                        className="px-4 py-3 theme-input rounded-lg theme-focus"
                        style={{ 
                          backgroundColor: 'var(--color-surface)',
                          border: '2px solid var(--color-border)',
                          color: 'var(--color-text)'
                        }}
                      />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      <input
                        type="email"
                        name="guardianEmail"
                        placeholder="Guardian Email"
                        value={formData.guardianEmail || ''}
                        onChange={handleChange}
                        className="px-4 py-3 theme-input rounded-lg theme-focus"
                        style={{ 
                          backgroundColor: 'var(--color-surface)',
                          border: '2px solid var(--color-border)',
                          color: 'var(--color-text)'
                        }}
                      />
                    </div>
                  </div>
                  
                  {/* Professional Information */}
                  <div id="auf-staff-professional" className="auf-form-section scroll-mt-28 border-t pt-4 mt-4">
                    <h3 className="text-lg font-semibold mb-4 theme-text-gradient">Professional Information</h3>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                      <input
                        type="text"
                        name="employeeId"
                        placeholder="Employee ID"
                        value={formData.employeeId || ''}
                        onChange={handleChange}
                        className="px-4 py-3 theme-input rounded-lg theme-focus"
                        style={{ 
                          backgroundColor: 'var(--color-surface)',
                          border: '2px solid var(--color-border)',
                          color: 'var(--color-text)'
                        }}
                      />
                      <input
                        type="number"
                        name="experienceYears"
                        placeholder="Years of Experience"
                        value={formData.experienceYears || ''}
                        onChange={handleChange}
                        className="px-4 py-3 theme-input rounded-lg theme-focus"
                        style={{ 
                          backgroundColor: 'var(--color-surface)',
                          border: '2px solid var(--color-border)',
                          color: 'var(--color-text)'
                        }}
                      />
                      <input
                        type="text"
                        name="availability"
                        placeholder="Availability Status"
                        value={formData.availability || ''}
                        onChange={handleChange}
                        className="px-4 py-3 theme-input rounded-lg theme-focus"
                        style={{ 
                          backgroundColor: 'var(--color-surface)',
                          border: '2px solid var(--color-border)',
                          color: 'var(--color-text)'
                        }}
                      />
                    </div>
                  </div>

                  {/* Additional Information */}
                  <div id="auf-staff-additional" className="auf-form-section scroll-mt-28 border-t pt-4 mt-4">
                <h3 className="text-lg font-semibold mb-4 theme-text-gradient">Additional Information</h3>
                
                <textarea
                  name="bio"
                  placeholder="Biography / About yourself"
                  value={formData.bio || ''}
                  onChange={handleChange}
                  className="w-full mb-4 px-4 py-3 theme-input rounded-lg theme-focus"
                  style={{ 
                    backgroundColor: 'var(--color-surface)',
                    border: '2px solid var(--color-border)',
                    color: 'var(--color-text)',
                    minHeight: '100px'
                  }}
                />
                
                <textarea
                  name="notes"
                  placeholder="Additional Notes"
                  value={formData.notes || ''}
                  onChange={handleChange}
                  className="w-full mb-4 px-4 py-3 theme-input rounded-lg theme-focus"
                  style={{ 
                    backgroundColor: 'var(--color-surface)',
                    border: '2px solid var(--color-border)',
                    color: 'var(--color-text)',
                    minHeight: '80px'
                  }}
                />
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <input
                  type="text"
                  name="department"
                  placeholder="Department"
                  value={formData.department || ''}
                  onChange={handleChange}
                  className="px-4 py-3 theme-input rounded-lg theme-focus"
                  style={{ 
                    backgroundColor: 'var(--color-surface)',
                    border: '2px solid var(--color-border)',
                    color: 'var(--color-text)'
                  }}
                />
                <input
                  type="text"
                  name="resumeUrl"
                  placeholder="Resume URL"
                  value={formData.resumeUrl || ''}
                  onChange={handleChange}
                  className="px-4 py-3 theme-input rounded-lg theme-focus"
                  style={{ 
                    backgroundColor: 'var(--color-surface)',
                    border: '2px solid var(--color-border)',
                    color: 'var(--color-text)'
                  }}
                />
              </div>
              
              {/* Location Section - Using new scalable component */}
              <LocationDropdowns
                selectedCountry={formData.country}
                selectedState={formData.state}
                selectedCity={formData.cityId}
                onCountryChange={(value) => dispatch({ type: 'UPDATE_FIELD', field: 'country', value })}
                onStateChange={(value) => dispatch({ type: 'UPDATE_FIELD', field: 'state', value })}
                onCityChange={(value) => dispatch({ type: 'UPDATE_FIELD', field: 'cityId', value })}
                className="border-t pt-4 mt-4"
              />
              
              <SearchableSelect
                name="heardFrom"
                value={formData.heardFrom || ''}
                onChange={handleChange}
                className="w-full mb-4"
                placeholder="How did you hear about us?"
                options={heardFromOptions}
                getOptionValue={(option) => (typeof option === 'object' ? option.id : option)}
                getOptionLabel={(option) => (typeof option === 'object' ? option.value : option)}
              />
              
              <textarea
                name="motivation"
                placeholder="What motivated you to join?"
                value={formData.motivation || ''}
                onChange={handleChange}
                className="w-full mb-4 px-4 py-3 theme-input rounded-lg theme-focus"
                style={{ 
                  backgroundColor: 'var(--color-surface)',
                  border: '2px solid var(--color-border)',
                  color: 'var(--color-text)',
                  minHeight: '100px'
                }}
              />
              
              {/* Profile Preference - Only for non-student roles */}
              {!isStudentRole && (
                <SearchableSelect
                  name="profilePreference"
                  value={formData.profilePreference}
                  onChange={handleChange}
                  className="w-full mb-4"
                  placeholder="Profile Choice or Preference"
                  options={[
                    { value: 'Physical Design', label: 'Physical Design' },
                    { value: 'Analog Design', label: 'Analog Design' },
                    { value: 'DV Profile', label: 'DV Profile' }
                  ]}
                />
              )}
            </div>
          )}
        </div>
          </>
        )}
    </AdminUserFormShell>
  );
};

export default AdminAddUserPage;